import { DialogComponent } from '@theme/dialog';
import { fetchSection } from '@theme/utilities';

export class SubscriptionUpsellModal extends DialogComponent {
  requiredRefs = ['dialog', 'productForm', 'acceptButton', 'declineButton'];

  constructor() {
    super();
    this.sessionStorageKey = 'subscription-upsell-shown';
    this.isProcessing = false;
    this._boundHandleCartAdded = this.handleCartAdded.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('cart:added', this._boundHandleCartAdded);

    if (this.refs.acceptButton) {
      this.refs.acceptButton.addEventListener('click', this.handleAcceptUpsell.bind(this));
    }

    if (this.refs.declineButton) {
      this.refs.declineButton.addEventListener('click', this.handleDeclineUpsell.bind(this));
    }

    this.setupSubscriptionOptions();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:added', this._boundHandleCartAdded);
  }

  setupSubscriptionOptions() {
    if (this.refs.subscriptionOptions) {
      const radioButtons = this.refs.subscriptionOptions.querySelectorAll('input[type="radio"]');
      radioButtons.forEach(radio => {
        radio.addEventListener('change', this.handleSubscriptionChange.bind(this));
      });
    }
  }

  handleSubscriptionChange(event) {
    const selectedPlanId = event.target.value;
    const selectedVariantId = event.target.dataset.variantId;

    if (this.refs.sellingPlanInput) {
      this.refs.sellingPlanInput.value = selectedPlanId;
    }

    if (this.refs.variantIdInput) {
      this.refs.variantIdInput.value = selectedVariantId;
    }

    if (this.refs.productForm) {
      this.refs.productForm.dataset.sellingPlan = selectedPlanId;
      this.refs.productForm.dataset.variantId = selectedVariantId;
    }

    if (this.currentUpsellConfig) {
      this.currentUpsellConfig.sellingPlanId = selectedPlanId;
      this.currentUpsellConfig.variantId = selectedVariantId;
    }
  }

  async handleCartAdded(event) {
    if (!this.shouldShowModal(event)) {
      return;
    }

    const upsellConfig = await this.getUpsellConfiguration(event.detail);

    if (!upsellConfig || !upsellConfig.enabled) {
      return;
    }

    await this.loadUpsellProduct(upsellConfig);

    this.showDialog();

    const showOnce = this.dataset.showOnce !== 'false';
    if (showOnce) {
      this.markAsShown(event.detail.productId);
    }
  }

  shouldShowModal(event) {
    if (this.isProcessing) {
      return false;
    }

    if (event.detail.isUpsell) {
      return false;
    }

    const triggerMode = this.dataset.triggerProducts || 'all';
    const productHandle = event.detail.productHandle;

    if (triggerMode === 'specific') {
      const specificProducts = (this.dataset.specificProducts || '').split(',').map(p => p.trim()).filter(p => p);

      if (specificProducts.length > 0 && !specificProducts.includes(productHandle)) {
        return false;
      }
    } else if (triggerMode === 'collections') {}

    const showOnce = this.dataset.showOnce !== 'false';

    if (showOnce) {
      const shownProducts = this.getShownProducts();
      if (shownProducts.includes(event.detail.productId)) {
        return false;
      }
    }

    return true;
  }

  async getUpsellConfiguration(productData) {
    const metafieldConfig = this.getMetafieldConfiguration();
    if (metafieldConfig) {
      return metafieldConfig;
    }

    const themeConfig = this.getThemeConfiguration();
    if (themeConfig) {
      return themeConfig;
    }

    return this.getDataAttributeConfiguration();
  }

  getMetafieldConfiguration() {
    const metafieldData = this.dataset.productMetafields;
    if (!metafieldData) return null;

    try {
      const metafields = JSON.parse(metafieldData);
      if (!metafields.subscription_upsell) return null;

      return {
        enabled: true,
        productId: metafields.subscription_upsell.product_id,
        variantId: metafields.subscription_upsell.variant_id,
        sellingPlanId: metafields.subscription_upsell.selling_plan_id,
        title: metafields.subscription_upsell.title,
        description: metafields.subscription_upsell.description
      };
    } catch (e) {
      return null;
    }
  }

  getThemeConfiguration() {
    const sectionSettings = window.theme?.settings?.subscriptionUpsell;
    if (!sectionSettings || !sectionSettings.enabled) return null;

    return {
      enabled: true,
      productId: sectionSettings.productId,
      variantId: sectionSettings.variantId,
      sellingPlanId: sectionSettings.sellingPlanId,
      title: sectionSettings.title,
      description: sectionSettings.description
    };
  }

  getDataAttributeConfiguration() {
    const isEnabled = this.dataset.enabled === 'true';

    if (!isEnabled) {
      return null;
    }

    if (!this.dataset.upsellProductId) {
      return null;
    }

    const hasMultiplePlans = this.dataset.monthlyPlan && this.dataset.annualPlan;

    const defaultVariantId = hasMultiplePlans
      ? (this.dataset.monthlyVariant || this.dataset.upsellVariantId)
      : this.dataset.upsellVariantId;

    return {
      enabled: true,
      productId: this.dataset.upsellProductId,
      variantId: defaultVariantId,
      sellingPlanId: hasMultiplePlans ? this.dataset.monthlyPlan : this.dataset.upsellSellingPlanId,
      monthlyVariantId: this.dataset.monthlyVariant,
      monthlyPlanId: this.dataset.monthlyPlan,
      annualVariantId: this.dataset.annualVariant,
      annualPlanId: this.dataset.annualPlan,
      hasMultiplePlans: hasMultiplePlans,
      title: this.dataset.upsellTitle,
      description: this.dataset.upsellDescription
    };
  }

  async loadUpsellProduct(config) {
    try {
      this.currentUpsellConfig = config;

      if (this.refs.productTitle) {
        this.refs.productTitle.textContent = config.title || 'Subscribe & Save';
      }

      if (this.refs.productDescription) {
        this.refs.productDescription.textContent = config.description || 'Get this product on subscription and save!';
      }

      if (this.refs.productForm) {
        this.refs.productForm.dataset.productId = config.productId;
        this.refs.productForm.dataset.variantId = config.variantId;
        this.refs.productForm.dataset.sellingPlan = config.sellingPlanId;
      }

      if (config.productId && !config.title) {
        await this.fetchProductDetails(config.productId);
      }
    } catch (error) {}
  }

  async fetchProductDetails(productId) {
    try {
      const response = await fetch(`/products/${productId}.js`);
      if (!response.ok) throw new Error('Failed to fetch product');

      const product = await response.json();

      if (this.refs.productTitle && product.title) {
        this.refs.productTitle.textContent = product.title;
      }

      if (this.refs.productImage && product.featured_image) {
        this.refs.productImage.src = product.featured_image;
        this.refs.productImage.alt = product.title;
      }

      if (this.refs.productPrice && product.price) {
        this.refs.productPrice.textContent = this.formatMoney(product.price);
      }
    } catch (error) {}
  }

  async handleAcceptUpsell() {
    if (this.isProcessing) return;

    this.isProcessing = true;
    this.refs.acceptButton?.classList.add('loading');

    try {
      const config = this.currentUpsellConfig;
      if (!config) throw new Error('No upsell configuration found');

      const currentVariantId = this.refs.variantIdInput?.value || config.variantId;
      const currentSellingPlan = this.refs.sellingPlanInput?.value || config.sellingPlanId;

      const cartItem = {
        id: currentVariantId,
        quantity: 1
      };

      if (currentSellingPlan) {
        cartItem.selling_plan = currentSellingPlan;
      }

      const cartSectionIds = [];

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartSectionIds.push(item.dataset.sectionId);
        }
      });

      const cartIcon = document.querySelector('cart-icon');
      if (cartIcon instanceof HTMLElement && cartIcon.dataset.sectionId) {
        cartSectionIds.push(cartIcon.dataset.sectionId);
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [cartItem],
          sections: cartSectionIds.join(',')
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add subscription to cart');
      }

      const result = await response.json();

      const { CartAddEvent, CartUpdateEvent } = await import('@theme/events');

      document.dispatchEvent(
        new CartAddEvent(result, currentVariantId, {
          source: 'subscription-upsell-modal',
          itemCount: 1,
          productId: config.productId,
          sections: result.sections,
          didError: false
        })
      );

      if (result.sections) {
        document.dispatchEvent(
          new CartUpdateEvent({}, 'subscription-upsell-modal', {
            itemCount: result.item_count || 1,
            source: 'subscription-upsell-modal',
            sections: result.sections
          })
        );
      }

      await this.closeDialog();

      this.showSuccessMessage();
    } catch (error) {
      this.showErrorMessage(error.message);
    } finally {
      this.isProcessing = false;
      this.refs.acceptButton?.classList.remove('loading');
    }
  }

  async handleDeclineUpsell() {
    this.trackUpsellEvent('declined');

    await this.closeDialog();
  }

  showSuccessMessage() {
    document.dispatchEvent(new CustomEvent('notification:show', {
      detail: {
        type: 'success',
        message: 'Subscription added to cart!'
      }
    }));
  }

  showErrorMessage(message) {
    document.dispatchEvent(new CustomEvent('notification:show', {
      detail: {
        type: 'error',
        message: message || 'Failed to add subscription to cart'
      }
    }));
  }

  trackUpsellEvent(action) {
    if (window.Shopify && window.Shopify.analytics) {
      window.Shopify.analytics.publish('subscription_upsell', {
        action: action,
        productId: this.currentUpsellConfig?.productId,
        sellingPlanId: this.currentUpsellConfig?.sellingPlanId
      });
    }
  }

  getShownProducts() {
    const data = sessionStorage.getItem(this.sessionStorageKey);
    return data ? JSON.parse(data) : [];
  }

  markAsShown(productId) {
    const shown = this.getShownProducts();
    if (!shown.includes(productId)) {
      shown.push(productId);
      sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(shown));
    }

    this.trackUpsellEvent('shown');
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    }).format(cents / 100);
  }
}

if (!customElements.get('subscription-upsell-modal')) {
  customElements.define('subscription-upsell-modal', SubscriptionUpsellModal);
}
